import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Mail, Calendar, Clock, Save, Trophy, Loader2, CalendarOff, Plus, X } from 'lucide-react';

/**
 * ScheduleSettingsDialog — "Schedule Settings": Referee Agency · Google Calendars ·
 * Game Durations.
 *
 * Extracted from GameSchedulePanel (2026-10-08) because every setting here is
 * ORG-WIDE, not per-sport: it only talks to
 *   GET/PUT /api/game-schedule/{org}/settings
 *   GET/PUT /api/game-schedule/{org}/team-durations
 *   GET/PUT /api/game-schedule/{org}/gcal-oauth/config  (+ start/test/disconnect)
 * That let it move to the Scheduler hub's COMMON row, above the sport tabs, where
 * it can be opened from any sub-tab instead of only the ones that mount the grid.
 * See SCHEDULER_ROLE_PAGE.md §4.
 *
 * Self-contained on purpose: it loads its own settings, durations, Google config
 * and sport list when it opens, so any caller can render it with just an orgId.
 *
 * `embedded` (2026-10-18, AJ): the SAME Google Calendars pane rendered inline on the
 * Tech Specialist's Tools → Google Calendars tab — one component, so the two can
 * never drift. Embedded mode shows only that pane and its Save writes only
 * `google_calendars` (never the referee, durations or blackouts).
 */
export default function ScheduleSettingsDialog({
  orgId, open, onOpenChange, initialTab = 'referee', isDark = true, onSaved, embedded = false,
}) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [tab, setTab] = useState(embedded ? 'gcal' : initialTab);
  const [loading, setLoading] = useState(false);
  // `loaded` gates Save: without it a failed GET followed by a Save would write
  // an empty referee/calendar block over good stored config.
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sports, setSports] = useState([]);
  const [settings, setSettings] = useState({ referee: {}, referee_by_sport: {}, google_calendars: {} });
  const [durations, setDurations] = useState([]);
  const [durationsMeta, setDurationsMeta] = useState({ default_minutes: 90, min: 30, max: 300 });
  const [gcalCfg, setGcalCfg] = useState(null);
  const [gcalForm, setGcalForm] = useState({ client_id: '', client_secret: '', expected_account_email: '' });
  const [gcalBusy, setGcalBusy] = useState('');
  // Blackout dates — days the org does not schedule games. Org-wide, like
  // everything else in this dialog. OL BookMe refuses to confirm onto one.
  const [blackouts, setBlackouts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestYear, setSuggestYear] = useState(new Date().getFullYear());
  const [holidayState, setHolidayState] = useState(null);   // null = use the org's saved state
  const [holidayStates, setHolidayStates] = useState([]);
  const [picked, setPicked] = useState({});                  // date -> true (holidays ticked to add)
  const [newBlackout, setNewBlackout] = useState({ date: '', label: '' });

  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const bg = isDark ? 'bg-slate-900 border-slate-700' : 'bg-white';
  const inputCls = isDark ? 'bg-slate-800 border-slate-600 text-white' : '';

  useEffect(() => { if (open && !embedded) setTab(initialTab || 'referee'); }, [open, initialTab, embedded]);

  const reloadGcalCfg = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/game-schedule/${orgId}/gcal-oauth/config`, { withCredentials: true });
    setGcalCfg(data || {});
    setGcalForm(p => ({
      ...p,
      client_id: data?.client_id || '',
      client_secret: '',
      expected_account_email: data?.expected_account_email || '',
    }));
    return data;
  }, [API_URL, orgId]);

  // Load everything when the dialog opens (and never while it is closed).
  useEffect(() => {
    if (!open || !orgId) return;
    let alive = true;
    setLoading(true);
    setLoaded(false);
    (async () => {
      const [stRes, durRes, spRes, blRes] = await Promise.all([
        axios.get(`${API_URL}/api/game-schedule/${orgId}/settings`, { withCredentials: true }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/api/game-schedule/${orgId}/team-durations`, { withCredentials: true }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/api/game-schedule/${orgId}/sports`, { withCredentials: true }).catch(e => ({ error: e })),
        axios.get(`${API_URL}/api/game-schedule/${orgId}/blackouts`, { withCredentials: true }).catch(e => ({ error: e })),
      ]);
      await reloadGcalCfg().catch(() => {});
      if (!alive) return;

      if (stRes.error) {
        toast.error('Could not load schedule settings — nothing was changed.');
      } else {
        setSettings({
          referee: stRes.data?.referee || {},
          referee_by_sport: stRes.data?.referee_by_sport || {},
          google_calendars: stRes.data?.google_calendars || {},
        });
        setLoaded(true);
      }
      if (!durRes.error) {
        setDurations(durRes.data?.teams || []);
        setDurationsMeta({
          default_minutes: durRes.data?.default_minutes ?? 90,
          min: durRes.data?.min ?? 30,
          max: durRes.data?.max ?? 300,
        });
      }
      if (!spRes.error) setSports(spRes.data?.sports || []);
      if (!blRes.error) setBlackouts(blRes.data?.blackouts || []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, orgId, API_URL, reloadGcalCfg]);

  const saveGcalCreds = async () => {
    setGcalBusy('save');
    try {
      const patch = { client_id: gcalForm.client_id, expected_account_email: gcalForm.expected_account_email };
      if (gcalForm.client_secret && gcalForm.client_secret.trim()) patch.client_secret = gcalForm.client_secret;
      await axios.put(`${API_URL}/api/game-schedule/${orgId}/gcal-oauth/config`, patch, { withCredentials: true });
      await reloadGcalCfg();
      toast.success('Google credentials saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setGcalBusy(''); }
  };

  const connectGcal = async () => {
    setGcalBusy('connect');
    try {
      const { data } = await axios.post(`${API_URL}/api/game-schedule/${orgId}/gcal-oauth/start`, {}, { withCredentials: true });
      if (data?.auth_url) window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start Google connect');
    } finally { setGcalBusy(''); }
  };

  const testGcal = async () => {
    setGcalBusy('test');
    try {
      const { data } = await axios.post(`${API_URL}/api/game-schedule/${orgId}/gcal-oauth/test`, {}, { withCredentials: true });
      const cals = data?.calendars || [];
      const ok = cals.filter(c => c.ok).length;
      const msg = cals.length
        ? `Tested ${cals.length} calendar(s): ${ok} OK, ${cals.length - ok} failed`
        : 'No calendars configured to test';
      (ok === cals.length && cals.length) ? toast.success(msg) : toast.error(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setGcalBusy(''); }
  };

  const disconnectGcal = async () => {
    setGcalBusy('disconnect');
    try {
      await axios.post(`${API_URL}/api/game-schedule/${orgId}/gcal-oauth/disconnect`, {}, { withCredentials: true });
      await reloadGcalCfg();
      toast.success('Google Calendar disconnected');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    } finally { setGcalBusy(''); }
  };

  const saveSettings = async () => {
    if (!loaded) return;
    setSaving(true);
    if (embedded) {
      // Tools → Google Calendars: only the calendar settings are on this page.
      try {
        await axios.put(`${API_URL}/api/game-schedule/${orgId}/settings`,
          { google_calendars: settings.google_calendars || {} }, { withCredentials: true });
        toast.success('Google Calendar settings saved');
        if (onSaved) onSaved(settings);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Save failed');
      } finally { setSaving(false); }
      return;
    }
    try {
      await axios.put(`${API_URL}/api/game-schedule/${orgId}/settings`, {
        referee_by_sport: Object.fromEntries(sports.map(sp => [sp.slug, refFor(sp.slug)])),
        google_calendars: settings.google_calendars || {},
      }, { withCredentials: true });
      if (durations.length) {
        const updates = durations
          .filter(t => t.id && Number.isInteger(t.minutes))
          .map(t => ({ id: t.id, game_duration_minutes: t.minutes }));
        if (updates.length) {
          await axios.put(`${API_URL}/api/game-schedule/${orgId}/team-durations`, { updates }, { withCredentials: true });
        }
      }
      await axios.put(`${API_URL}/api/game-schedule/${orgId}/blackouts`,
        { blackouts }, { withCredentials: true });
      toast.success('Schedule settings saved');
      if (onSaved) onSaved(settings);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const tabBtn = (v, label, Icon) => (
    <button onClick={() => setTab(v)}
      className={`px-3 py-1.5 text-xs rounded-md ${tab === v ? 'bg-amber-600 text-white' : muted}`}
      data-testid={`settings-tab-${v}`}>
      <Icon className="h-3 w-3 mr-1 inline" /> {label}
    </button>
  );

  // Referee agency, one per sport (AJ 2026-10-20). A sport with no saved contact of its own
  // shows the org-wide one it uses today until it is changed and saved.
  const refFor = (code) => (settings.referee_by_sport || {})[code] || { ...(settings.referee || {}), _inherited: true };
  const setRef = (code, field, value) =>
    setSettings(p => {
      const cur = (p.referee_by_sport || {})[code] || { ...(p.referee || {}) };
      const next = { ...cur, [field]: value };
      delete next._inherited;
      return { ...p, referee_by_sport: { ...(p.referee_by_sport || {}), [code]: next } };
    });

  // A function, not an inner component: an inner component would remount the inputs
  // (and drop focus) on every keystroke.
  const wrap = (children) => (embedded
    ? <div className={`rounded-lg border p-4 ${bg}`} data-testid="gcal-embedded">{children}</div>
    : (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={`max-w-2xl ${bg}`}>{children}</DialogContent>
      </Dialog>
    ));

  return wrap(
    <>
        {embedded ? (
          <div className="mb-3">
            <div className={`text-base font-semibold ${text}`}>Google Calendars</div>
            <p className={`text-[11px] ${muted}`}>
              The same settings as Scheduler → Settings → Google Calendars. They apply to the whole organization — every sport.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className={text}>Schedule Settings</DialogTitle>
            </DialogHeader>
            <p className={`text-[11px] -mt-1 ${muted}`}>
              These apply to the whole organization — every sport.
            </p>
            <div className="flex gap-2 mb-3 border-b pb-2" data-testid="settings-tabs">
              {tabBtn('referee', 'Referee Agency', Mail)}
              {tabBtn('gcal', 'Google Calendars', Calendar)}
              {tabBtn('durations', 'Game Durations', Clock)}
              {tabBtn('blackouts', 'Blackout Dates', CalendarOff)}
            </div>
          </>
        )}

        {loading && (
          <div className={`flex items-center gap-2 text-xs ${muted}`} data-testid="settings-loading">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading settings…
          </div>
        )}

        {tab === 'referee' && (
          <div className="space-y-3 py-1 max-h-[55vh] overflow-y-auto pr-1" data-testid="settings-referee-pane">
            <p className={`text-xs ${muted}`}>
              One referee agency per sport. Publish notifies the game's sport's agency for every game whose
              <Mail className="h-3 w-3 inline mx-0.5" /> icon is not yet green; edited rows reset the icon, so the next
              publish sends only the changes.
            </p>
            {sports.map(sp => {
              const r = refFor(sp.slug);
              return (
                <div key={sp.slug} className={`rounded-lg border p-3 space-y-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                  data-testid={`ref-sport-${sp.slug}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${text}`}>{sp.label || sp.slug.toUpperCase()}</span>
                    {r._inherited && (r.agency_name || r.email) && (
                      <span className={`text-[10px] ${muted}`}>using the organization's current agency — edit and Save to set {sp.label}'s own</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className={`text-xs ${muted}`}>Agency name</Label>
                      <Input value={r.agency_name || ''} onChange={e => setRef(sp.slug, 'agency_name', e.target.value)}
                        placeholder="e.g. Arbiter Sports" className={`${inputCls} h-8`} data-testid={`ref-${sp.slug}-agency`} />
                    </div>
                    <div>
                      <Label className={`text-xs ${muted}`}>Contact name</Label>
                      <Input value={r.contact_name || ''} onChange={e => setRef(sp.slug, 'contact_name', e.target.value)} className={`${inputCls} h-8`} />
                    </div>
                    <div>
                      <Label className={`text-xs ${muted}`}>Email</Label>
                      <Input type="email" value={r.email || ''} onChange={e => setRef(sp.slug, 'email', e.target.value)}
                        placeholder="assignor@refagency.com" className={`${inputCls} h-8`} data-testid={`ref-${sp.slug}-email`} />
                    </div>
                    <div>
                      <Label className={`text-xs ${muted}`}>Phone (SMS)</Label>
                      <Input type="tel" value={r.phone || ''} onChange={e => setRef(sp.slug, 'phone', e.target.value)}
                        placeholder="+1 555 123 4567" className={`${inputCls} h-8`} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className={`flex items-center gap-2 text-xs ${text} cursor-pointer`}>
                      <input type="checkbox" checked={!!r.email_enabled} onChange={e => setRef(sp.slug, 'email_enabled', e.target.checked)} />
                      Send email notifications
                    </label>
                    <label className={`flex items-center gap-2 text-xs ${text} cursor-pointer`}>
                      <input type="checkbox" checked={!!r.sms_enabled} onChange={e => setRef(sp.slug, 'sms_enabled', e.target.checked)} />
                      Send SMS notifications
                    </label>
                  </div>
                </div>
              );
            })}
            {sports.length === 0 && !loading && <div className={`text-xs ${muted}`}>No sports set up yet.</div>}
          </div>
        )}

        {tab === 'gcal' && (
          <div className={`space-y-3 py-1 ${embedded ? '' : 'max-h-[60vh] overflow-y-auto'}`} data-testid="settings-gcal-pane">
            <div className={`rounded-lg border p-3 ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200'}`}
              data-testid="gcal-connection-card">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-blue-500" />
                <span className={`text-sm font-semibold ${text}`}>Google Connection</span>
                {gcalCfg?.connected
                  ? <Badge className="bg-emerald-600 text-[10px]" data-testid="gcal-status-connected">Connected</Badge>
                  : <Badge className="bg-slate-500/70 text-[10px]" data-testid="gcal-status-disconnected">Not connected</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={`text-xs ${muted}`}>Client ID</Label>
                  <Input value={gcalForm.client_id}
                    onChange={e => setGcalForm(p => ({ ...p, client_id: e.target.value }))}
                    placeholder="xxxx.apps.googleusercontent.com"
                    className={inputCls} data-testid="gcal-client-id" />
                </div>
                <div>
                  <Label className={`text-xs ${muted}`}>Client Secret</Label>
                  <Input type="password" value={gcalForm.client_secret}
                    onChange={e => setGcalForm(p => ({ ...p, client_secret: e.target.value }))}
                    placeholder={gcalCfg?.client_secret_set ? '••• saved' : 'GOCSPX-…'}
                    className={inputCls} data-testid="gcal-client-secret" />
                </div>
                <div className="col-span-2">
                  <Label className={`text-xs ${muted}`}>Expected account email</Label>
                  <Input value={gcalForm.expected_account_email}
                    onChange={e => setGcalForm(p => ({ ...p, expected_account_email: e.target.value }))}
                    placeholder="admin@southwakesabres.org"
                    className={inputCls} data-testid="gcal-expected-email" />
                </div>
              </div>
              <div className={`text-xs mt-2 ${gcalCfg?.connected ? 'text-emerald-500' : muted}`}
                data-testid="gcal-connection-status">
                {gcalCfg?.connected
                  ? `Connected as ${gcalCfg.connected_account_email}${gcalCfg.connected_at ? ' · ' + new Date(gcalCfg.connected_at).toLocaleDateString() : ''}`
                  : 'Not connected'}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button size="sm" onClick={saveGcalCreds} disabled={gcalBusy === 'save'}
                  className="bg-slate-600 hover:bg-slate-700 text-white text-xs h-7" data-testid="gcal-save-btn">Save</Button>
                <Button size="sm" onClick={connectGcal} disabled={gcalBusy === 'connect'}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7" data-testid="gcal-connect-btn">Connect Google Calendar</Button>
                <Button size="sm" onClick={testGcal} disabled={gcalBusy === 'test' || !gcalCfg?.connected}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7" data-testid="gcal-test-btn">Test connection</Button>
                <Button size="sm" variant="outline" onClick={disconnectGcal} disabled={gcalBusy === 'disconnect' || !gcalCfg?.connected}
                  className="text-xs h-7" data-testid="gcal-disconnect-btn">Disconnect</Button>
              </div>
            </div>
            <div className={`text-xs p-2 rounded border ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
              Each sport uses its own game calendar and practice calendar — no sharing. Publish routes games to the sport's <strong>game</strong> calendar and practices to its <strong>practice</strong> calendar. Practices never go to WordPress.
              <div className="mt-1 opacity-80" data-testid="gcal-connection-banner">
                {gcalCfg?.connected
                  ? `Connected as ${gcalCfg.connected_account_email}. Publishes write to Google immediately.`
                  : 'Google is not connected. Publishes to Google will queue until you connect.'}
              </div>
            </div>
            {/* Org events calendar (_org.events) — banquets, rallies, picture day.
                Its own calendar so a family subscribing to games is not sent the
                banquet. OL_BOOKME.md §6f. */}
            <div className={`rounded-lg border p-3 ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200'}`}
              data-testid="gcal-org-events-card">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-purple-400" />
                <span className={`text-sm font-semibold ${text}`}>Org events</span>
                <Badge variant="outline" className="text-[9px]">all sports</Badge>
              </div>
              <Label className={`text-xs ${muted}`}>Events calendar ID</Label>
              <Input
                value={settings.google_calendars?._org?.events?.calendar_id || ''}
                onChange={e => setSettings(prev => {
                  const gc = { ...(prev.google_calendars || {}) };
                  const cur = { ...(gc._org || {}) };
                  cur.events = { ...(cur.events || {}), calendar_id: e.target.value };
                  gc._org = cur;
                  return { ...prev, google_calendars: gc };
                })}
                placeholder="xxxxx@group.calendar.google.com"
                className={inputCls} data-testid="gcal-org-events-id" />
              <p className={`text-[11px] mt-1 ${muted}`}>
                Banquets, rallies and picture day push here. A sport can override it below.
              </p>
            </div>

            {sports.map(sp => {
              const slot = settings.google_calendars?.[sp.slug] || {};
              const updateCal = (kind, field, value) => {
                setSettings(prev => {
                  const gc = { ...(prev.google_calendars || {}) };
                  const cur = { ...(gc[sp.slug] || {}) };
                  cur[kind] = { ...(cur[kind] || {}), [field]: value };
                  gc[sp.slug] = cur;
                  return { ...prev, google_calendars: gc };
                });
              };
              return (
                <div key={sp.slug} className={`rounded-lg border p-3 ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200'}`}
                  data-testid={`gcal-sport-${sp.slug}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="h-4 w-4 text-amber-500" />
                    <span className={`text-sm font-semibold ${text}`}>{sp.label}</span>
                    {sp.wp_synced ? <Badge className="bg-emerald-600 text-[10px]">WP synced</Badge>
                      : <Badge className="bg-amber-500/80 text-[10px]">WP pending</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className={`text-xs ${muted} flex items-center gap-1`}>
                        <Trophy className="h-3 w-3" /> Game calendar ID
                      </Label>
                      <Input value={slot.game?.calendar_id || ''}
                        onChange={e => updateCal('game', 'calendar_id', e.target.value)}
                        placeholder="xxxxx@group.calendar.google.com"
                        className={inputCls} data-testid={`gcal-game-id-${sp.slug}`} />
                    </div>
                    <div>
                      <Label className={`text-xs ${muted} flex items-center gap-1`}>
                        <Calendar className="h-3 w-3" /> Practice calendar ID
                      </Label>
                      <Input value={slot.practice?.calendar_id || ''}
                        onChange={e => updateCal('practice', 'calendar_id', e.target.value)}
                        placeholder="xxxxx@group.calendar.google.com"
                        className={inputCls} data-testid={`gcal-practice-id-${sp.slug}`} />
                    </div>
                    <div className="col-span-2">
                      <Label className={`text-xs ${muted} flex items-center gap-1`}>
                        <Calendar className="h-3 w-3" /> Events calendar ID
                        <span className={`${muted} font-normal`}>(optional — overrides the org events calendar)</span>
                      </Label>
                      <Input value={slot.events?.calendar_id || ''}
                        onChange={e => updateCal('events', 'calendar_id', e.target.value)}
                        placeholder="leave blank to use the org events calendar"
                        className={inputCls} data-testid={`gcal-events-id-${sp.slug}`} />
                    </div>
                  </div>
                </div>
              );
            })}
            {sports.length === 0 && !loading && (
              <div className={`text-xs ${muted}`}>Add a sport first to configure its Google Calendars.</div>
            )}
          </div>
        )}

        {tab === 'durations' && (
          <div className="space-y-3 py-1 max-h-[55vh] overflow-y-auto" data-testid="settings-durations-pane">
            <p className={`text-xs ${muted}`}>
              Google Calendar event length for each roster team (Teams ▸ Team Levels), per sport. Used for every published game.
              Range {durationsMeta.min}–{durationsMeta.max} min. Existing published events keep their
              current length until the game is re-pushed.
            </p>
            {(() => {
              const seen = [];
              durations.forEach(t => { if (t.sport && !seen.includes(t.sport)) seen.push(t.sport); });
              return seen;
            })().map(sp => {
              const rows = durations.filter(t => t.sport === sp);
              if (!rows.length) return null;
              const spLabel = rows[0].sport_label || sp;
              return (
                <div key={sp} className="space-y-1.5">
                  <div className={`text-xs font-semibold uppercase tracking-wide ${text}`}>{spLabel}</div>
                  {rows.map(t => (
                    <div key={t.id} className="flex items-center justify-between gap-3">
                      <span className={`text-sm ${text}`}>{t.label} <span className={muted}>({t.level})</span></span>
                      <div className="flex items-center gap-1">
                        <Input type="number" min={durationsMeta.min} max={durationsMeta.max} step={5}
                          value={t.minutes}
                          onChange={e => {
                            const v = parseInt(e.target.value, 10);
                            setDurations(prev => prev.map(r => r.id === t.id
                              ? { ...r, minutes: Number.isNaN(v) ? r.minutes : v } : r));
                          }}
                          className={`h-7 w-20 text-xs text-right ${isDark ? 'bg-slate-800 border-slate-600 text-slate-100' : ''}`}
                          data-testid={`duration-input-${t.id}`} />
                        <span className={`text-xs ${muted}`}>min</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            {durations.length === 0 && !loading && (
              <div className={`text-xs ${muted}`}>No configurable teams found for this organization.</div>
            )}
          </div>
        )}

        {tab === 'blackouts' && (
          <div className="space-y-3 py-1 max-h-[55vh] overflow-y-auto" data-testid="settings-blackouts-pane">
            <p className={`text-xs ${muted}`}>
              Days this organization does not schedule games. OL BookMe will not confirm a game onto a
              blackout date unless you override it, and the Scheduler flags them on proposals.
            </p>

            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label className={`text-xs ${muted}`}>Date</Label>
                <Input type="date" value={newBlackout.date} className={`${inputCls} h-8`}
                  onChange={e => setNewBlackout(b => ({ ...b, date: e.target.value }))}
                  data-testid="blackout-new-date" />
              </div>
              <div className="flex-1 min-w-[160px]">
                <Label className={`text-xs ${muted}`}>Reason</Label>
                <Input value={newBlackout.label} className={`${inputCls} h-8`}
                  placeholder="e.g. Spring break, Exams"
                  onChange={e => setNewBlackout(b => ({ ...b, label: e.target.value }))}
                  data-testid="blackout-new-label" />
              </div>
              <Button size="sm" className="h-8 text-xs bg-slate-600 hover:bg-slate-700 text-white"
                disabled={!newBlackout.date}
                onClick={() => {
                  if (!newBlackout.date) return;
                  setBlackouts(prev => prev.some(b => b.date === newBlackout.date) ? prev
                    : [...prev, { date: newBlackout.date, label: newBlackout.label.trim() || 'Blackout', source: 'custom' }]
                      .sort((a, b) => a.date.localeCompare(b.date)));
                  setNewBlackout({ date: '', label: '' });
                }}
                data-testid="blackout-add">
                <Plus className="h-3 w-3 mr-1" />Add
              </Button>
            </div>

            <div className="space-y-1">
              {blackouts.length === 0 && (
                <div className={`text-xs ${muted}`}>No blackout dates yet.</div>
              )}
              {blackouts.map(b => (
                <div key={b.date} className={`flex items-center gap-2 text-xs px-2 py-1 rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                  data-testid={`blackout-row-${b.date}`}>
                  <CalendarOff className="h-3 w-3 text-amber-500" />
                  <span className={text}>{b.date}</span>
                  <span className={muted}>{b.label}</span>
                  {b.source === 'holiday' && <Badge variant="outline" className="text-[9px]">holiday</Badge>}
                  <button className={`ml-auto ${muted} hover:text-red-500`}
                    onClick={() => setBlackouts(prev => prev.filter(x => x.date !== b.date))}
                    title="Remove" data-testid={`blackout-remove-${b.date}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>

            <div className={`border-t pt-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className={`text-xs font-semibold mb-1 ${text}`}>US holidays</div>
              <div className="flex items-end gap-2 mb-2 flex-wrap">
                <div>
                  <Label className={`text-xs ${muted}`}>Year</Label>
                  <Input type="number" value={suggestYear} className={`${inputCls} h-8 w-24`}
                    onChange={e => setSuggestYear(parseInt(e.target.value, 10) || suggestYear)}
                    data-testid="blackout-suggest-year" />
                </div>
                <div>
                  <Label className={`text-xs ${muted}`}>State holidays too</Label>
                  <select value={holidayState ?? ''} onChange={e => setHolidayState(e.target.value)}
                    className={`block h-8 rounded-md border px-2 text-xs ${isDark ? 'bg-slate-800 border-slate-600 text-slate-100' : 'bg-white border-slate-300'}`}
                    data-testid="blackout-state">
                    <option value="">Federal only</option>
                    {holidayStates.map(st => <option key={st.code} value={st.code}>{st.name}</option>)}
                  </select>
                </div>
                <Button size="sm" variant="outline" className={`h-8 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                  onClick={async () => {
                    try {
                      const { data } = await axios.get(
                        `${API_URL}/api/game-schedule/${orgId}/blackouts/suggest`,
                        { params: { year: suggestYear, ...(holidayState !== null ? { state: holidayState } : {}) }, withCredentials: true });
                      setSuggestions(data?.suggestions || []);
                      setHolidayStates(data?.states || []);
                      setHolidayState(data?.state || '');
                      setPicked({});
                    } catch { toast.error('Could not load holidays.'); }
                  }}
                  data-testid="blackout-suggest">Show holidays</Button>
              </div>
              <p className={`text-[11px] mb-1 ${muted}`}>
                From the public holiday calendar (the <i>holidays</i> library). Tick the ones this organization does not play on, then Add.
              </p>
              {suggestions.length > 0 && (() => {
                const open = suggestions.filter(h => !blackouts.some(b => b.date === h.date));
                const n = open.filter(h => picked[h.date]).length;
                return (
                  <div className="space-y-1">
                    <div className="flex items-center gap-3 text-[11px]">
                      <button className="text-blue-600 hover:underline" onClick={() => setPicked(Object.fromEntries(open.map(h => [h.date, true])))}>Select all</button>
                      <button className="text-blue-600 hover:underline" onClick={() => setPicked({})}>Clear</button>
                      <Button size="sm" disabled={!n} className="ml-auto h-7 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => {
                          setBlackouts(prev => [...prev, ...open.filter(h => picked[h.date]).map(h => ({ date: h.date, label: h.label, source: 'holiday' }))]
                            .sort((a, b) => a.date.localeCompare(b.date)));
                          setPicked({});
                        }} data-testid="blackout-add-picked">Add {n || ''} selected</Button>
                    </div>
                    {suggestions.map(h => {
                      const added = blackouts.some(b => b.date === h.date);
                      return (
                        <label key={h.date + h.label} className={`flex items-center gap-2 text-xs px-2 py-1 rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'} ${added ? 'opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" disabled={added} checked={added || !!picked[h.date]}
                            onChange={e => setPicked(pk => ({ ...pk, [h.date]: e.target.checked }))}
                            data-testid={`blackout-pick-${h.date}`} />
                          <span className={text}>{h.date}</span>
                          <span className={muted}>{h.label}</span>
                          {added && <span className={`ml-auto text-[10px] ${muted}`}>blacked out</span>}
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t mt-3">
          {!embedded && <Button variant="ghost" onClick={() => onOpenChange(false)} className={muted}>Cancel</Button>}
          <Button onClick={saveSettings} disabled={!loaded || saving}
            title={loaded ? '' : 'Settings could not be loaded — saving is disabled so stored values are not overwritten.'}
            className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="settings-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
          </Button>
        </div>
    </>
  );
}
